import React, { useState, useRef, useEffect } from 'react'
import axios from 'axios';
import Select from 'react-select';
import { baseUrl, statusList } from "../../../comman/config";
import { useNavigate, Link } from 'react-router-dom';
import { menuAboveTable } from "../../../comman/selectMenu";

// Add/Edit Role. A role is a name and the operations it holds -- see backend/utils/roles.js for
// what each operation gates.
//
// The operation list comes from the backend rather than a copy kept here, so the form, the list
// and the checks that enforce them cannot drift apart.
export default function RoleMasterForm({ detail, page = 'role_master' }) {

    const [formData, setFormData] = useState({
        name: '',
        operations: [],
        status: 1,
    });
    const [operationOptions, setOperationOptions] = useState([]);
    const [validationErrors, setValidationErrors] = useState({
        name: false,
        operations: false,
    });
    const [saveError, setSaveError] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const formRef = useRef(null);

    const navigate = useNavigate();
    const role = String(localStorage.getItem('userRole'));

    useEffect(() => {
        const getOperations = async () => {
            try {
                const response = await axios.get(`${baseUrl}/role_master/operations`);
                setOperationOptions(response.data?.data ?? []);
            } catch (error) {
                console.log(error);
            }
        };
        getOperations();
    }, []);

    // Options arrive from their own request, so the stored CSV is turned into options only once
    // both are in hand -- otherwise an edit would open with the operations blank.
    useEffect(() => {
        if (!detail) return;
        const roleDetail = detail[0];
        const stored = String(roleDetail.operations ?? '').split(',').filter((entry) => entry !== '');
        setFormData({
            name: roleDetail.name || '',
            operations: stored.map((operation) => operationOptions.find((option) => option.value === operation)
                ?? { value: operation, label: operation }),
            status: Number(roleDetail.status ?? 1),
        });
    }, [detail, operationOptions]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newValidationErrors = {
            name: formData.name.trim() === '',
            operations: formData.operations.length === 0,
        };
        setValidationErrors(newValidationErrors);
        if (Object.values(newValidationErrors).some((error) => error)) {
            const firstInvalidInput = formRef.current.querySelector('.invalid');
            if (firstInvalidInput) {
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaveError('');
        try {
            setPopupOpen(true);
            const url = detail ? `${page}/edit/${detail[0].id}` : `${page}/add`;
            await axios.post(`${baseUrl}/${url}`, {
                role,
                formData: {
                    ...formData,
                    operations: formData.operations.map((operation) => operation.value),
                },
            });
            setPopupOpen(false);
            navigate(`/${page}/list`);
        } catch (error) {
            setPopupOpen(false);
            console.log(error);
            setSaveError(error?.response?.data?.msg ?? 'Could not save the role.');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
        setValidationErrors((prevErrors) => ({ ...prevErrors, [name]: false }));
    };

    return (
        <form ref={formRef}>
            <div className="space-y-12">
                <div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-8">
                        <div className="sm:col-span-2">
                            <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">Role Name *</label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    placeholder="Role Name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className={`block w-full rounded-md border ${validationErrors.name ? 'border-red-500 invalid' : 'border-gray-300'} py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                                />
                            </div>
                            {validationErrors.name && <div className="text-red-500">Please enter a role name.</div>}
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="operations" className="block text-sm font-medium leading-6 text-gray-900">Operation *</label>
                            <div className={`mt-2 ${validationErrors.operations ? 'invalid' : ''}`}>
                                <Select
                                    {...menuAboveTable}
                                    isMulti
                                    inputId="operations"
                                    classNamePrefix="select"
                                    className="basic-multi-select"
                                    value={formData.operations}
                                    options={operationOptions}
                                    onChange={(selected) => {
                                        setFormData((prevData) => ({ ...prevData, operations: selected ?? [] }));
                                        setValidationErrors((prevErrors) => ({ ...prevErrors, operations: false }));
                                    }}
                                    placeholder="Select operations"
                                />
                            </div>
                            {validationErrors.operations
                                && <div className="text-red-500">Please select at least one operation.</div>}
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="status" className="block text-sm font-medium leading-6 text-gray-900">Status *</label>
                            <div className="mt-2">
                                <Select
                                    {...menuAboveTable}
                                    inputId="status"
                                    classNamePrefix="select"
                                    value={statusList.find((option) => option.value === Number(formData.status))}
                                    options={statusList}
                                    onChange={(selected) => setFormData((prevData) => ({ ...prevData, status: selected.value }))}
                                    isClearable={false}
                                />
                            </div>
                            <div className="text-xs text-gray-500 mt-1">An inactive role holds no operations at all.</div>
                        </div>
                    </div>
                    {saveError && <div className="text-red-500 mt-4">{saveError}</div>}
                </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-x-6">
                <Link to={`/${page}/list`}>
                    <button type="button" className="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
                </Link>
                <button
                    onClick={handleSubmit}
                    disabled={popupOpen}
                    type="submit"
                    className="rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                    Submit
                </button>
            </div>
        </form>
    )
}
